import { useState } from 'react'
import { Link } from 'react-router-dom'
import { BRAND, CONTACT_EMAIL, PLANS } from './brand'
import './marketing.css'

// Public marketing landing page (route "/"). Copy follows the client's 2026-09
// direction: 6 features + 6-step workflow (roadmap items tagged "Coming soon"),
// confirmed pricing, and labeled placeholder testimonials (no fabricated
// reviews). CTAs flow into the real app: /register + /login.
export function LandingPage() {
  return (
    <div className="mkt">
      <MarketingNav />
      <Hero />
      <Features />
      <HowItWorks />
      <Testimonials />
      <Pricing />
      <CtaBand />
      <MarketingFooter />
    </div>
  )
}

function Logo({ light }: { light?: boolean }) {
  return (
    <span className="mkt-logo" style={light ? { color: '#fff' } : undefined}>
      <span className="mkt-logo-mark">
        <ShieldIcon />
      </span>
      {BRAND}
    </span>
  )
}

function Soon() {
  return <span className="mkt-soon">Coming soon</span>
}

export function MarketingNav() {
  const [open, setOpen] = useState(false)
  return (
    <nav className="mkt-nav">
      <div className="mkt-container mkt-nav-inner">
        <Link to="/"><Logo /></Link>
        <div className="mkt-nav-links">
          <a href="/#features">Features</a>
          <a href="/#how">How It Works</a>
          <a href="/#testimonials">Testimonials</a>
          <a href="/#pricing">Pricing</a>
        </div>
        <div className="mkt-nav-cta">
          <Link to="/login" className="mkt-nav-login">Log in</Link>
          <Link to="/register" className="mkt-btn mkt-btn-primary">Get Started Free</Link>
        </div>
        <button className="mkt-nav-toggle" onClick={() => setOpen((v) => !v)} aria-label="Menu">
          <MenuIcon />
        </button>
      </div>
      {open && (
        <div className="mkt-container mkt-nav-mobile">
          <a href="/#features" onClick={() => setOpen(false)}>Features</a>
          <a href="/#how" onClick={() => setOpen(false)}>How It Works</a>
          <a href="/#pricing" onClick={() => setOpen(false)}>Pricing</a>
          <Link to="/login" onClick={() => setOpen(false)}>Log in</Link>
          <Link to="/register" className="mkt-btn mkt-btn-primary" onClick={() => setOpen(false)}>Get Started Free</Link>
        </div>
      )}
    </nav>
  )
}

function Hero() {
  return (
    <header className="mkt-hero">
      <div className="mkt-container">
        <span className="mkt-kicker">Built specifically for trades</span>
        <h1>Simplify property damage documentation on every job site</h1>
        <p className="lead">
          Cloud-based damage tracking that helps HVAC, plumbing, and electrical contractors document
          property damage, coordinate their team, and protect against liability disputes.
        </p>
        <div className="mkt-hero-cta">
          <Link to="/register" className="mkt-btn mkt-btn-primary mkt-btn-lg">Get Started Free</Link>
          <a href="#how" className="mkt-btn mkt-btn-ghost mkt-btn-lg">See How It Works</a>
        </div>
        <p className="mkt-hero-note">Forever-free plan · No credit card required</p>
        <Shot
          src="/marketing/shot-dashboard.png"
          mobileSrc="/marketing/shot-dashboard-m.png"
          title={`${BRAND} — Dashboard`}
          alt="Resilience dashboard showing damage counts, repair cost, a trend chart, and top technicians and subcontractors."
        />
      </div>
    </header>
  )
}

// Real app screenshot in a browser-window frame on desktop; on phones it swaps
// to the app's actual mobile view (and the desktop chrome is hidden via CSS) so
// the shot reads as a real phone screen.
function Shot({ src, mobileSrc, title, alt }: { src: string; mobileSrc?: string; title: string; alt: string }) {
  return (
    <div className="mkt-shot">
      <div className="mkt-shot-bar">
        <span className="mkt-dot" style={{ background: '#ff5f57' }} />
        <span className="mkt-dot" style={{ background: '#febc2e' }} />
        <span className="mkt-dot" style={{ background: '#28c840' }} />
        <span className="mkt-shot-title">{title}</span>
      </div>
      <picture>
        {mobileSrc && <source media="(max-width: 640px)" srcSet={mobileSrc} />}
        <img className="mkt-shot-img" src={src} alt={alt} loading="lazy" />
      </picture>
    </div>
  )
}

function Features() {
  const items: { icon: JSX.Element; title: string; body: string; soon?: boolean }[] = [
    {
      icon: <CameraIcon />,
      title: 'Property Damage Documentation',
      body:
        'Techs capture close-up and wide-shot photos from the mobile link before they pick up a tool — time-stamped and tied to the address. Every case gets an airtight, unalterable record.',
    },
    {
      icon: <ChatIcon />,
      title: 'Automated Client Management',
      soon: true,
      body:
        'The moment a report is logged, a brand-matched message goes to the homeowner with a self-scheduling link — so nobody is left waiting and your office is never blindsided by a call.',
    },
    {
      icon: <WrenchIcon />,
      title: 'Subcontractor Management',
      body:
        'Keep your vetted trade network in one place — contacts, specialties, and payout ranges. Assign the right sub to a case in a couple of taps and track spend as it happens.',
    },
    {
      icon: <ChartIcon />,
      title: 'Technician Coaching Portal',
      soon: true,
      body:
        'Every technician gets a profile with full damage history and performance trends, so you can spot patterns and coach the crews that need it most.',
    },
    {
      icon: <BoltIcon />,
      title: '1-Click Emergency Dispatching',
      soon: true,
      body:
        'High-severity job? Hand the entire case to a managed dispatcher with one button — human voice contact, mitigation, and repair coordination handled for you.',
    },
    {
      icon: <SignatureIcon />,
      title: 'Digital Liability Release',
      soon: true,
      body:
        'Once the repair is verified, the homeowner signs a digital release on their phone. It converts to a PDF and archives to the case for a clean legal trail.',
    },
  ]
  return (
    <section className="mkt-section" id="features">
      <div className="mkt-container">
        <div className="mkt-head">
          <span className="mkt-kicker">Power features</span>
          <h2>Engineered to protect your business on every call</h2>
          <p>Field-ready damage tracking that keeps your dispatchers, techs, and business owners fully connected.</p>
        </div>
        <div className="mkt-grid">
          {items.map((f) => (
            <div className="mkt-feature" key={f.title}>
              <div className="mkt-feature-icon">{f.icon}</div>
              <h3>{f.title}{f.soon && <Soon />}</h3>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function HowItWorks() {
  const steps: { t: string; b: string; soon?: boolean }[] = [
    {
      t: 'Instant Field Reporting & Team Alert',
      b: 'The tech uploads two photos and picks a damage category from the mobile link. The case is created instantly and your office dashboard is alerted in real time.',
    },
    {
      t: 'Automated Homeowner Outreach & Self-Scheduling',
      soon: true,
      b: 'The system texts the homeowner a self-scheduling link to pick a repair window — and flags the case for a human call if they are upset or do not respond.',
    },
    {
      t: 'Subcontractor Matching & Broadcast',
      soon: true,
      b: 'Once a window is picked, the job is matched to approved local subs by zip code, trade, and payout — the first vetted sub to accept gets it.',
    },
    {
      t: 'Real-Time Team Coordination & Visibility',
      b: 'Managers, owners, and dispatchers watch the full case lifecycle on one live dashboard, with private internal notes so everyone has full context.',
    },
    {
      t: 'Subcontractor Work Execution & Photo Verification',
      soon: true,
      b: 'The sub completes the repair and cannot mark it done without uploading clear "after" photos through their link.',
    },
    {
      t: 'Digital Liability Release & Case Closure',
      soon: true,
      b: 'The homeowner signs a digital satisfaction release, it is saved as a PDF to the case file, and the case closes clean.',
    },
  ]
  return (
    <section className="mkt-section soft" id="how">
      <div className="mkt-container">
        <div className="mkt-head">
          <span className="mkt-kicker">Process overview</span>
          <h2>How {BRAND} runs a case from photo to closeout</h2>
          <p>One connected workflow from the first field photo to a signed, closed case. Steps marked “coming soon” are rolling out through 2026.</p>
        </div>
        <div className="mkt-figrow">
          <Shot
            src="/marketing/shot-cases.png"
            mobileSrc="/marketing/shot-cases-m.png"
            title="Open Damages"
            alt="Open damages list with case numbers, addresses, incident dates, severity, and descriptions."
          />
          <Shot
            src="/marketing/shot-case.png"
            mobileSrc="/marketing/shot-case-m.png"
            title="Case detail"
            alt="Case detail view with customer info, damage details, status, assigned subcontractor, photos, and costs."
          />
        </div>
        <div className="mkt-timeline">
          {steps.map((s, i) => (
            <div className="mkt-tl-step" key={s.t}>
              <div className="mkt-tl-n">{i + 1}</div>
              <div className="mkt-tl-body">
                <h3>{s.t}{s.soon && <Soon />}</h3>
                <p>{s.b}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Testimonials() {
  return (
    <section className="mkt-section" id="testimonials">
      <div className="mkt-container">
        <div className="mkt-head">
          <span className="mkt-kicker">Customer stories</span>
          <h2>Trusted by trade business owners</h2>
          <p>We’re gathering results from our first fleets. Real customer quotes will live here soon.</p>
        </div>
        <div className="mkt-quotes">
          {[0, 1].map((i) => (
            <div className="mkt-quote" key={i}>
              <span className="mkt-placeholder-tag">Testimonial coming soon</span>
              <blockquote>
                A real customer quote will go here — a specific result in the owner’s own words. This card
                is styled and ready; we’ll drop in the genuine review and name as soon as it’s in.
              </blockquote>
              <div className="who">
                <div className="avatar" />
                <div>
                  <b>Your first customers</b>
                  <span>Trade business owners</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Pricing() {
  return (
    <section className="mkt-section soft" id="pricing">
      <div className="mkt-container">
        <div className="mkt-head">
          <span className="mkt-kicker">Pricing</span>
          <h2>Simple, transparent plans for your team</h2>
          <p>Start free, forever. Upgrade only when you’re ready for automation and multi-location. Features marked “soon” are on the roadmap.</p>
        </div>
        <div className="mkt-plans">
          {PLANS.map((p) => {
            const mailto = p.ctaHref.startsWith('mailto:')
            const btnClass = `mkt-btn ${p.featured ? 'mkt-btn-primary' : 'mkt-btn-ghost'}`
            return (
              <div className={`mkt-plan${p.featured ? ' featured' : ''}`} key={p.name}>
                {p.featured && <span className="tag">Most Popular</span>}
                <h3>{p.name}</h3>
                <div className="blurb">{p.blurb}</div>
                <div className="price">{p.price}<span> {p.period}</span></div>
                <div className="mkt-plan-note">{p.note}</div>
                {mailto ? (
                  <a href={p.ctaHref} className={btnClass}>{p.cta}</a>
                ) : (
                  <Link to={p.ctaHref} className={btnClass}>{p.cta}</Link>
                )}
                <ul>
                  {p.features.map((f) => (
                    <li key={f.t}><CheckIcon />{f.t}{f.soon && <Soon />}</li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

function CtaBand() {
  return (
    <section className="mkt-section mkt-cta-band">
      <div className="mkt-container">
        <h2>Stop paying for pre-existing damage</h2>
        <p>Protect your technicians, secure your profits, and gain complete operational visibility on every site call. Setup takes less than 10 minutes.</p>
        <div className="mkt-hero-cta">
          <Link to="/register" className="mkt-btn mkt-btn-white mkt-btn-lg">Get Started Free</Link>
          <a href="#pricing" className="mkt-btn mkt-btn-outline-white mkt-btn-lg">See Pricing</a>
        </div>
      </div>
    </section>
  )
}

function FLink({ href, children }: { href: string; children: string }) {
  // Real internal pages use client routing; hash + mailto links use a plain <a>
  // so the browser handles the scroll / mail client.
  if (href.startsWith('/') && !href.includes('#')) return <Link to={href}>{children}</Link>
  return <a href={href}>{children}</a>
}

export function MarketingFooter() {
  const cols: { h: string; links: { label: string; href: string }[] }[] = [
    {
      h: 'Product',
      links: [
        { label: 'Features', href: '/#features' },
        { label: 'How It Works', href: '/#how' },
        { label: 'Pricing', href: '/#pricing' },
      ],
    },
    {
      h: 'Resources',
      links: [
        { label: 'Help', href: `mailto:${CONTACT_EMAIL}` },
        { label: 'Contact Us', href: `mailto:${CONTACT_EMAIL}` },
      ],
    },
    {
      h: 'Legal',
      links: [
        { label: 'Privacy Policy', href: '/privacy' },
        { label: 'Terms of Service', href: '/terms' },
      ],
    },
  ]
  return (
    <footer className="mkt-footer">
      <div className="mkt-container">
        <div className="mkt-footer-top">
          <div>
            <Logo light />
            <p className="mkt-footer-about">Damage documentation and case management built for field trade service teams.</p>
          </div>
          {cols.map((c) => (
            <div className="mkt-footer-col" key={c.h}>
              <h4>{c.h}</h4>
              {c.links.map((l) => (
                <FLink href={l.href} key={l.label}>{l.label}</FLink>
              ))}
            </div>
          ))}
        </div>
        <div className="mkt-footer-bottom">
          <span>© {new Date().getFullYear()} Resilience Damages. All rights reserved.</span>
          <span><Link to="/privacy">Privacy Policy</Link> · <Link to="/terms">Terms of Service</Link></span>
        </div>
      </div>
    </footer>
  )
}

/* ---- Inline icons (no asset deps) ---- */
function ShieldIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5l8-3z" fill="currentColor" /></svg>
}
function MenuIcon() {
  return <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" /></svg>
}
function CheckIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
}
function CameraIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 8a2 2 0 012-2h1.5l1-2h5l1 2H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" /><circle cx="12" cy="12.5" r="3.2" /></svg>
}
function ChatIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 5h16a1 1 0 011 1v10a1 1 0 01-1 1H9l-4 3v-3H4a1 1 0 01-1-1V6a1 1 0 011-1z" strokeLinejoin="round" /><path d="M8 10h8M8 13h5" strokeLinecap="round" /></svg>
}
function WrenchIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14.7 6.3a4 4 0 00-5.2 5.2L3 18l3 3 6.5-6.5a4 4 0 005.2-5.2l-2.5 2.5-2.5-.5-.5-2.5 2.5-2.5z" strokeLinejoin="round" /></svg>
}
function ChartIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 4v16h16" strokeLinecap="round" /><path d="M8 14l3-3 3 2 4-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
}
function BoltIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" strokeLinejoin="round" /></svg>
}
function SignatureIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 19c3 0 3-10 6-10s2 7 4 7 2-4 5-4" strokeLinecap="round" strokeLinejoin="round" /><path d="M3 22h18" strokeLinecap="round" /></svg>
}
